import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { CoreAPI_URLs } from 'constants/http.const';
import axios from '../../http/axios';

// Define the shape of general settings (adjust as needed)
export interface GeneralSettingsState {
    country: string;
    language: string;
    isInsuranceModuleEnabled: boolean;
    isPharmacyModuleEnabled: boolean;
    isOfficeVisitModuleEnabled: boolean;
    isSystemSimplified: boolean;
    isPatientUserEnabled: boolean;
    videochatEnabledTime?: number;
    videochatEnabledAfterAppointmentTime?: number;
    currency: string;
    defaultAppointmentDuration: number;
    loading: boolean;
    error?: string;
    showModal: boolean;
    initialized: boolean;
}

const initialState: GeneralSettingsState = {
    country: '',
    language: '',
    isInsuranceModuleEnabled: false,
    isPharmacyModuleEnabled: false,
    isOfficeVisitModuleEnabled: false,
    isSystemSimplified: false,
    isPatientUserEnabled: false,
    videochatEnabledTime: undefined,
    videochatEnabledAfterAppointmentTime: undefined,
    currency: '',
    defaultAppointmentDuration: 30,
    loading: false,
    error: undefined,
    showModal: false,
    initialized: false,
};

/**
 * Async thunk to fetch general settings from the API
 */
export const fetchGeneralSettings = createAsyncThunk(
    'generalSettings/fetch',
    async (_, thunkAPI) => {
        try {
            const response = await axios.get(CoreAPI_URLs.GENERAL_SETTINGS);
            return response.data;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.response?.data?.message || error.message || 'Error fetching general settings');
        }
    }
);

/**
 * Async thunk to save general settings to the API
 */
export const saveGeneralSettings = createAsyncThunk(
    'generalSettings/save',
    async (settings: Partial<GeneralSettingsState>, thunkAPI) => {
        try {
            const response = await axios.post(CoreAPI_URLs.GENERAL_SETTINGS, settings);
            return response.data;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.response?.data?.message || error.message || 'Error saving general settings');
        }
    }
);

const generalSettingsSlice = createSlice({
    name: 'generalSettings',
    initialState,
    reducers: {
        setGeneralSettings(state, action: PayloadAction<Partial<GeneralSettingsState>>) {
            Object.assign(state, action.payload);
        },
        clearGeneralSettings(state) {
            Object.assign(state, initialState);
        },
        setShowModal(state, action: PayloadAction<boolean>) {
            state.showModal = action.payload;
        },
        setInitialized(state, action: PayloadAction<boolean>) {
            state.initialized = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchGeneralSettings.pending, (state) => {
                state.loading = true;
                state.error = undefined;
            })
            .addCase(fetchGeneralSettings.fulfilled, (state, action: PayloadAction<any>) => {
                const data = action.payload;
                // Check if settings exist
                if (data && data.country && data.language && data.currency) {
                    Object.assign(state, data);
                    state.showModal = false;
                    state.initialized = true;
                } else {
                    // No settings found, show modal
                    state.showModal = true;
                    state.initialized = true;
                }
                state.loading = false;
            })
            .addCase(fetchGeneralSettings.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string || action.error.message || 'Error fetching settings';
                // If fetch fails (e.g., no settings exist), show modal
                state.showModal = true;
                state.initialized = true;
            })
            .addCase(saveGeneralSettings.pending, (state) => {
                state.loading = true;
                state.error = undefined;
            })
            .addCase(saveGeneralSettings.fulfilled, (state, action: PayloadAction<any>) => {
                Object.assign(state, action.payload);
                state.loading = false;
                state.showModal = false;
            })
            .addCase(saveGeneralSettings.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string || action.error.message || 'Error saving settings';
            });
    },
});

export const { setGeneralSettings, clearGeneralSettings, setShowModal, setInitialized } = generalSettingsSlice.actions;
export const selectGeneralSettings = (state: RootState) => state.generalSettings;
export const selectShowModal = (state: RootState) => state.generalSettings.showModal;
export const selectIsInitialized = (state: RootState) => state.generalSettings.initialized;
export default generalSettingsSlice.reducer;